import React, { FC, useEffect, useMemo, useRef } from 'react';
import { FaBullhorn, FaCreditCard, FaIdBadge, FaRegFileAlt } from 'react-icons/fa';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, TabItem, Tabs } from '@/lib';
import { Loading } from '@/components/loaders';
import { RouteConstants } from '@/features/router/RouteConstants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useCompanyValues } from '@/providers';
import { useGetCampaignsCountV2Query } from '@/services/endpoints/campaigns';
import { useGetWorkspaceConnectorsQuery } from '@/services/endpoints/workspace-connectors';
import { SMS_CONNECTOR_NAME } from '../Sms/constants';
import { SmsWorkspaceProvider, useSmsWorkspaceData } from '../Sms/context/SmsWorkspaceContext';
import {
  SMS_CAMPAIGNS_PATTERNS,
  SMS_PACKAGES_PATTERNS,
  SMS_SENDER_MUTATION_PATTERNS,
  SMS_SENDERS_PATTERNS,
  SMS_TEMPLATE_MUTATION_PATTERNS,
  SMS_TEMPLATES_PATTERNS,
} from '../Sms/smsWorkspaceRoutes';
import { CampaignsSmsRoutesV2 } from './CampaignsSmsRoutesV2';
import { CampaignsCreditBalanceBar } from '../credits';
import ErrorBoundary from '@/components/ErrorBoundary';

const ConnectorNotConnectedBlock: FC = () => {
  const history = useHistory();
  const { t } = useTranslation('campaign');

  return (
    <div className='flex flex-col items-center justify-center gap-6 rounded-lg border border-gray-400 py-20'>
      <h2 className='text-4xl font-semibold text-slate-600'>{t('smsWorkspace.noSmsConnectorTitle', { connectorName: SMS_CONNECTOR_NAME })}</h2>
      <div className='flex flex-col items-center gap-4'>{t('smsWorkspace.noSmsConnectorDescription', { connectorName: SMS_CONNECTOR_NAME })}</div>
      <FaRegFileAlt className='size-12 text-gray-400' />
      <Button onClick={() => history.push(RouteConstants.destinations)}>{t('goToDestinationsText')}</Button>
    </div>
  );
};

const SmsWorkspaceInit = () => {
  const history = useHistory();
  const location = useLocation();
  const workspace = useWorkspace();
  const previousPathnameRef = useRef(location.pathname);

  const { data: campaignsCount } = useGetCampaignsCountV2Query({ workspaceId: workspace.id, providerType: 'SMS' });
  const {
    templates: { templatesCount, isLoading: isTemplatesLoading, reload: reloadTemplates },
    senders: { sendersCount, isLoading: isSendersLoading, reload: reloadSenders },
    hasEnabledSenders,
  } = useSmsWorkspaceData();

  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = location.pathname;

    const returnedToTemplateList =
      matchPath(location.pathname, { path: RouteConstants.smsTemplates, exact: true }) &&
      SMS_TEMPLATE_MUTATION_PATTERNS.some((pattern) => matchPath(previousPathname, { path: pattern, exact: true }));

    if (returnedToTemplateList) {
      void reloadTemplates();
    }

    const returnedToSenderList =
      matchPath(location.pathname, { path: RouteConstants.smsSenders, exact: true }) &&
      SMS_SENDER_MUTATION_PATTERNS.some((pattern) => matchPath(previousPathname, { path: pattern, exact: true }));

    if (returnedToSenderList) {
      void reloadSenders();
    }
  }, [location.pathname, reloadSenders, reloadTemplates]);

  const sendersTabCount = !hasEnabledSenders || isSendersLoading ? 'N/A' : sendersCount;

  const tabItems = useMemo<TabItem[]>(
    () => [
      {
        id: 'campaigns',
        title: 'campaign.whatsappWorkspace.campaigns' as const,
        icon: <FaBullhorn className='size-4' />,
        count: campaignsCount ?? 'N/A',
        element: <CampaignsSmsRoutesV2 />,
      },
      {
        id: 'templates',
        title: 'campaign.whatsappWorkspace.templates' as const,
        icon: <FaRegFileAlt className='size-4' />,
        count: isTemplatesLoading ? 'N/A' : templatesCount,
        element: <CampaignsSmsRoutesV2 />,
      },
      {
        id: 'senders',
        title: 'campaign.smsWorkspace.sendersTab' as const,
        icon: <FaIdBadge className='size-4' />,
        count: sendersTabCount,
        element: <CampaignsSmsRoutesV2 />,
      },
      {
        id: 'packages',
        title: 'campaign.whatsappWorkspace.packages' as const,
        icon: <FaCreditCard className='size-4' />,
        count: 'N/A',
        element: <CampaignsSmsRoutesV2 />,
      },
    ],
    [campaignsCount, isTemplatesLoading, sendersTabCount, templatesCount]
  );

  const activeTabId = useMemo(() => {
    const isCampaignRoute = SMS_CAMPAIGNS_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isCampaignRoute) {
      return 'campaigns';
    }

    const isTemplateRoute = SMS_TEMPLATES_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isTemplateRoute) {
      return 'templates';
    }

    const isSenderRoute = SMS_SENDERS_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isSenderRoute) {
      return 'senders';
    }

    const isPackageRoute = SMS_PACKAGES_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isPackageRoute) {
      return 'packages';
    }

    return 'campaigns';
  }, [location.pathname]);

  const currentTab = tabItems.find((item) => item.id === activeTabId) ?? tabItems[0];

  const handleSelectTab = (tabItem: TabItem) => {
    if (tabItem.id === 'campaigns') {
      history.push(RouteConstants.smsCampaigns);
      return;
    }

    if (tabItem.id === 'templates') {
      history.push(RouteConstants.smsTemplates);
      return;
    }

    if (tabItem.id === 'senders') {
      history.push(RouteConstants.smsSenders);
      return;
    }

    if (tabItem.id === 'packages') {
      history.push(RouteConstants.smsPackages);
    }
  };

  return (
    <div className='grid w-full grid-cols-1 gap-4'>
      <CampaignsCreditBalanceBar channel='sms' />
      <Tabs items={tabItems} currentTab={currentTab.title} onSelectTab={handleSelectTab} />
    </div>
  );
};

export const CampaignsSmsWorkspaceV2: FC = () => {
  const workspace = useWorkspace();
  const { currentLanguage } = useCompanyValues();

  const { data: customConnectors, isLoading: isConnectorsLoading } = useGetWorkspaceConnectorsQuery(
    { workspaceId: workspace.id, lang: currentLanguage },
    { skip: !workspace.id }
  );

  const smsConnector = customConnectors?.find((connector) => connector.name === SMS_CONNECTOR_NAME);
  const isSmsAuthenticated = smsConnector?.isAuthenticated ?? false;

  if (isConnectorsLoading) {
    return <Loading />;
  }

  if (!isSmsAuthenticated || !smsConnector?.id) {
    return <ConnectorNotConnectedBlock />;
  }

  return (
    <SmsWorkspaceProvider connectorId={smsConnector.id}>
      <div className='grid w-full grid-cols-1'>
        <ErrorBoundary>
          <SmsWorkspaceInit />
        </ErrorBoundary>
      </div>
    </SmsWorkspaceProvider>
  );
};
